import { ConnectionListResult, FeatureFlagsContext, GridSortProps, MaxCountsResultData, PaginatedQueryWrapper } from '@tableau/data-catalog-shared';
import React = require('react');

import { Messages } from '../../../gen-src/Localize';
import {
  DatabaseDownstreamVirtualConnectionTablesFilteredByTableQuery,
  DatabaseDownstreamVirtualConnectionTablesFilteredByTableQueryVariables,
} from '../../../gen-src/queries/DatabaseDownstreamVirtualConnectionTablesFilteredByTableQuery';
import {
  DatabaseDownstreamVirtualConnectionTablesQuery,
  DatabaseDownstreamVirtualConnectionTablesQueryVariables,
} from '../../../gen-src/queries/DatabaseDownstreamVirtualConnectionTablesQuery';
import {
  VirtualConnectionTablesDisplayGridRowDataFragment,
} from '../../../gen-src/queries/VirtualConnectionTablesDisplayGridRowDataFragment';
import { VirtualConnectionTableSortOrder } from '../../../gen-src/queries/globalTypes';
import {
  DatabaseDownstreamVirtualConnectionTablesFilteredByTableQueryGql,
} from '../../queries/DatabaseDownstreamVirtualConnectionTablesFilteredByTableQueryGql';
import {
  DatabaseDownstreamVirtualConnectionTablesQueryGql,
} from '../../queries/DatabaseDownstreamVirtualConnectionTablesQueryGql';
import { VirtualConnectionTablesDisplayGrid } from '../sharedDisplayGrid/VirtualConnectionTablesDisplayGrid';
import {
  VirtualConnectionTablesValidSortFields,
} from '../sorting/sortProviders/VirtualConnectionTablesGridSortProvider';
import {
  getRowDataWithMaxCountsForVirtualConnectionTable,
} from '../table/functions/VirtualConnectionTableRowDataFunctions';
import { VirtualConnectionTablesDisplayGridRowData } from "../table/types/VirtualConnectionTablesDisplayGridRowData";

type QueryVariables = DatabaseDownstreamVirtualConnectionTablesQueryVariables;
type QueryResults = DatabaseDownstreamVirtualConnectionTablesQuery;
type QueryResultListNode = VirtualConnectionTablesDisplayGridRowDataFragment;
type GridRowData = VirtualConnectionTablesDisplayGridRowData;

type QueryVariablesWithFilter = DatabaseDownstreamVirtualConnectionTablesFilteredByTableQueryVariables;
type QueryResultsWithFilter = DatabaseDownstreamVirtualConnectionTablesFilteredByTableQuery;

function selectConnectionListResult(resultData: QueryResults): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.databases && resultData.databases[0] &&
    resultData.databases[0].downstreamVirtualConnectionTablesConnection;
}

function selectConnectionListResultForFilteredQuery(
  resultData: QueryResultsWithFilter
): ConnectionListResult<QueryResultListNode> | null {
  return resultData && resultData.tables && resultData.tables[0] &&
    resultData.tables[0].downstreamVirtualConnectionTablesConnection;
}

function selectMaxCountsResult(resultData: QueryResults): MaxCountsResultData {
  return resultData && resultData.databases && resultData.databases[0];
}

function selectMaxCountsResultForFilteredQuery(resultData: QueryResultsWithFilter): MaxCountsResultData {
  return resultData && resultData.tables && resultData.tables[0];
}

function getRowDataForVirtualConnectionTable(
  virtualConnectionTable: VirtualConnectionTablesDisplayGridRowDataFragment,
  resultData: QueryResults,
): VirtualConnectionTablesDisplayGridRowData {
  return getRowDataWithMaxCountsForVirtualConnectionTable(virtualConnectionTable, selectMaxCountsResult(resultData));
}

function getRowDataForVirtualConnectionTableForFilteredQuery(
  virtualConnectionTable: VirtualConnectionTablesDisplayGridRowDataFragment,
  resultData: QueryResultsWithFilter,
): VirtualConnectionTablesDisplayGridRowData {
  return getRowDataWithMaxCountsForVirtualConnectionTable(virtualConnectionTable, selectMaxCountsResultForFilteredQuery(resultData));
}

export type DatabaseDownstreamVirtualConnectionTablesQueryGridProps =
  GridSortProps<VirtualConnectionTablesValidSortFields> & {
    readonly databaseId: string;
    readonly filteredTableId: string | null;
    readonly rowCount: number | null;
  };

export function DatabaseDownstreamVirtualConnectionTablesQueryGrid(props: DatabaseDownstreamVirtualConnectionTablesQueryGridProps): JSX.Element {
  const { publishedConnectionsLineageEnabled } = React.useContext(FeatureFlagsContext);

  const emptyGridMessage = Messages.EmptyDownstreamVirtualConnectionTablesLineageGridMessage();
  const emptyFilteredGridMessage = Messages.EmptyDownstreamFilteredVirtualConnectionTablesLineageGridMessage();

  return props.filteredTableId ? (
    <PaginatedQueryWrapper<QueryVariablesWithFilter, QueryResultsWithFilter, QueryResultListNode, GridRowData>
      queryGql={DatabaseDownstreamVirtualConnectionTablesFilteredByTableQueryGql}
      partialQueryVariables={{
        tableId: props.filteredTableId,
        orderBy: props.orderBy as VirtualConnectionTableSortOrder,
        publishedConnectionsLineageEnabled
      }}
      selectConnectionListResult={selectConnectionListResultForFilteredQuery}
      mapResultNode={getRowDataForVirtualConnectionTableForFilteredQuery}
    >
      {({ rowStore }) =>
        <VirtualConnectionTablesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyFilteredGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />
      }
    </PaginatedQueryWrapper>
  ) : (
    <PaginatedQueryWrapper<QueryVariables, QueryResults, QueryResultListNode, GridRowData>
      queryGql={DatabaseDownstreamVirtualConnectionTablesQueryGql}
      partialQueryVariables={{
        databaseId: props.databaseId,
        orderBy: props.orderBy as VirtualConnectionTableSortOrder,
        publishedConnectionsLineageEnabled
      }}
      selectConnectionListResult={selectConnectionListResult}
      mapResultNode={getRowDataForVirtualConnectionTable}
    >
      {({ rowStore }) =>
        <VirtualConnectionTablesDisplayGrid
          rowStore={rowStore}
          emptyGridMessage={emptyGridMessage}
          rowCount={props.rowCount}
          orderBy={props.orderBy}
          updateSortOrder={props.updateSortOrder}
        />
      }
    </PaginatedQueryWrapper>
  );
}
